import { useDispatch, useSelector } from 'react-redux';
import { Card, CardActionArea, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AssessmentSteps, setAssessmentStep } from '../redux/reducers/assessmentReducer';
import assessmentIcon from '../assets/images/assessments-green.svg';
import manageActivitiesIcon from '../assets/images/manage-activities-green.svg';
import managementFrameworkIcon from '../assets/images/management-framework-green.svg';
import settingsIcon from '../assets/images/settings-green.svg';
import LoadingIndicator from '../components/LoadingIndicator';
import { LoadStatus } from '../constants/constants';
import { initializeByOrganization as initializeFindings } from '../redux/reducers/findingsReducer';
import { selectOrganizationLoadStatus, selectSelectedOrganizationData } from '../redux/reducers/organizationReducer';

function HomeView() {
  const organizationLoadStatus = useSelector(selectOrganizationLoadStatus);
  const { t } = useTranslation();

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { organizationId } = useSelector(selectSelectedOrganizationData);

  const navigateToAssess = () => {
    dispatch(setAssessmentStep(AssessmentSteps.ASSESSMENT_SELECTION));
    navigate('/assess');
  };

  const navigateToActivities = () => {
    dispatch(initializeFindings(organizationId));
    navigate('/activities');
  };
  const navigateToFrameworks = () => {
    navigate('/frameworks');
  };
  const navigateToOrganization = () => {
    navigate('/organization');
  };

  return (
    <>
      <Stack
        direction="column"
        spacing={4}
        sx={{ overflow: 'auto', height: '100vh', maxWidth: '1200px', padding: '24px 24px 60px 24px' }}
      >
        <Stack direction="column" spacing={1}>
          <Typography id='home-title' variant="title">{t('home.title')}</Typography>
          <Typography variant="homeSubtitle">{t('home.description')}</Typography>
        </Stack>
        <Stack direction="row" spacing={4} sx={{ padding: '0px 4px', width: '100%' }}>
          <Card id='assessments-view' onClick={navigateToAssess} square={false} elevation={4} sx={{ maxWidth: '50%' }}>
            <CardActionArea
              sx={{
                height: '100%',
                padding: '24px 20px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
              }}
            >
              <Stack direction="column" spacing={3} justifyContent="flex-start">
                <Stack direction="row" alignItems="center" spacing={3}>
                  <img src={assessmentIcon} alt="assessment icon" />
                  <Typography variant="homeSectionTitle">{t('home.assessmentsSectionTitle')}</Typography>
                </Stack>
                <Typography variant="regular">{t('home.assessmentsSectionDescription.part1')}</Typography>
                <Typography variant="regular">{t('home.assessmentsSectionDescription.part2')}</Typography>
                <Typography variant="regular">{t('home.assessmentsSectionDescription.part3')}</Typography>
              </Stack>
            </CardActionArea>
          </Card>
          <Card id='activities-view' onClick={navigateToActivities} square={false} elevation={4} sx={{ maxWidth: '50%' }}>
            <CardActionArea
              sx={{
                height: '100%',
                padding: '24px 20px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
              }}
            >
              <Stack direction="column" spacing={3} justifyContent="flex-start">
                <Stack direction="row" alignItems="center" spacing={3}>
                  <img src={manageActivitiesIcon} alt="activities icon" />
                  <Typography variant="homeSectionTitle">{t('home.manageActivitiesTitle')}</Typography>
                </Stack>
                <Typography variant="regular">{t('home.manageActivitiesSectionDescription.part1')}</Typography>
                <Typography variant="regular">{t('home.manageActivitiesSectionDescription.part2')}</Typography>
                <Typography variant="regular">{t('home.manageActivitiesSectionDescription.part3')}</Typography>
              </Stack>
            </CardActionArea>
          </Card>
        </Stack>
        <Stack direction="column" spacing={1}>
          <Typography variant="homeSectionTitle">{t('home.organizationTitle')}</Typography>
          <Typography variant="regular">{t('home.organizationDescription')}</Typography>
        </Stack>
        <Stack direction="row" spacing={4} sx={{ padding: '0px 4px', width: '100%' }}>
          <Card id='frameworks-view' onClick={navigateToFrameworks} square={false} elevation={4} sx={{ maxWidth: '50%' }}>
            <CardActionArea
              sx={{
                height: '100%',
                padding: '24px 20px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
              }}
            >
              <Stack direction="column" spacing={3} justifyContent="flex-start">
                <Stack direction="row" alignItems="center" spacing={3}>
                  <img src={managementFrameworkIcon} alt="framework icon" />
                  <Typography variant="homeSectionTitle">{t('home.managementFrameworkTitle')}</Typography>
                </Stack>
                <Typography variant="regular">{t('home.managementFrameworkSectionDescription.part1')}</Typography>
                <Typography variant="regular">{t('home.managementFrameworkSectionDescription.part2')}</Typography>
                <Typography variant="regular">{t('home.managementFrameworkSectionDescription.part3')}</Typography>
              </Stack>
            </CardActionArea>
          </Card>

          <Card id='organization-view' onClick={navigateToOrganization} square={false} elevation={4} sx={{ maxWidth: '50%' }}>
            <CardActionArea
              sx={{
                height: '100%',
                padding: '24px 20px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
              }}
            >
              <Stack direction="column" spacing={3} justifyContent="flex-start">
                <Stack direction="row" alignItems="center" spacing={3}>
                  <img src={settingsIcon} alt="settings icon" />
                  <Typography variant="homeSectionTitle">{t('home.settingsTitle')}</Typography>
                </Stack>
                <Typography variant="regular">{t('home.settingsSectionDescription.part1')}</Typography>
                <Typography variant="regular">{t('home.settingsSectionDescription.part2')}</Typography>
                <Typography variant="regular">{t('home.settingsSectionDescription.part3')}</Typography>
              </Stack>
            </CardActionArea>
          </Card>
        </Stack>
      </Stack>
      {organizationLoadStatus !== LoadStatus.SUCCESS && <LoadingIndicator />}
    </>
  );
}

export default HomeView;
